import Message, { MessageStatus } from "src/app/models/Message";
import Account from "src/app/models/Account";
import { Room } from "src/app/models/Chat";
import * as server from "src/api/server/message";
import * as message from "src/db/serviceMessage";
import { messageApiClient, ShopeeMessageHelper, LazadaMessageHelper, WhatsAppMessageHelper, UnifiedMessageRequest } from "./index";

// Platforms supported by the unified API
type UnifiedPlatform = "whatsapp" | "shopee" | "lazada";

// Interface for platform-specific message helpers
interface MessageHelper {
  validate(message: Message, userId: string): void;
  buildRequestBody(userId: string, message: Message): UnifiedMessageRequest;
}

// Helper lookup for all unified platforms
const PLATFORM_HELPERS: Record<UnifiedPlatform, MessageHelper> = {
  whatsapp: WhatsAppMessageHelper,
  shopee: ShopeeMessageHelper,
  lazada: LazadaMessageHelper,
};

type SendMessageCallback = (message: Message, error: any) => void;

interface SendMessageParams {
  message: Message;
  companyID: string;
  account: Account | undefined;
  room: Room | undefined;
  callback: SendMessageCallback;
  isRetry: boolean;
}

/**
 * MessageSendingService - Handles all message sending operations
 * Consolidates WhatsApp, Shopee, Lazada, and marketplace fallback logic
 */
export class MessageSendingService {
  /**
   * Main entry point for sending messages
   */
  static async sendMessage(params: SendMessageParams): Promise<void> {
    const { message: msg, account, room, callback, isRetry } = params;

    if (!account || !msg?.collaboration?.id || !msg?.room?.id) {
      const errorMsg = !account ? "No account found." : "No collaboration or room ID found.";
      msg.status = MessageStatus.failed;
      msg.resultMessage = errorMsg;
      msg.error = { errorCode: 'CLIENT_VALIDATION', errorMessage: errorMsg };
      return;
    }

    const channel = msg.channel;

    switch (channel) {
      case "whatsapp":
      case "shopee":
      case "lazada":
        return this.sendUnifiedMessage(params, channel as UnifiedPlatform);
      default:
        return this.sendMarketplaceFallback(params);
    }
  }

  /**
   * Send message via unified API (WhatsApp, Shopee, Lazada)
   * Consolidated method that handles all platforms using the v2 unified endpoint
   */
  private static async sendUnifiedMessage(
    params: SendMessageParams,
    platform: UnifiedPlatform
  ): Promise<void> {
    const { message: msg, account, room, callback, isRetry } = params;

    // Get sender ID based on platform
    let senderId: string;
    let userId: string;

    if (platform === "whatsapp") {
      // WhatsApp uses phone numbers
      senderId = (account as any)?.phone || (account as any)?.whatsappNumber || "";
      userId = msg.customerPhoneNumber || "";

      if (!userId) {
        msg.status = MessageStatus.failed;
        msg.resultMessage = "No customer phone number found.";
        msg.error = { errorCode: 'CLIENT_VALIDATION', errorMessage: 'No customer phone number found.' };
        await this.saveFailedMessage(msg, isRetry, callback);
        return;
      }
    } else {
      // Marketplace uses shop ID and buyer ID
      senderId = account?.marketplaceShopId || "";
      userId = room?.userSearchKey || "";

      if (!senderId) {
        return this.sendMarketplaceFallback(params);
      }
    }

    if (!senderId) {
      msg.status = MessageStatus.failed;
      msg.resultMessage = `No sender ID found for ${platform}`;
      msg.error = { errorCode: 'CLIENT_VALIDATION', errorMessage: `No sender ID found for ${platform}` };
      await this.saveFailedMessage(msg, isRetry, callback);
      return;
    }

    if (!userId) {
      msg.status = MessageStatus.failed;
      msg.resultMessage = `User ID not found for ${platform} message`;
      msg.error = { errorCode: 'CLIENT_VALIDATION', errorMessage: `User ID not found for ${platform} message` };
      await this.saveFailedMessage(msg, isRetry, callback);
      return;
    }

    try {
      // Get platform-specific helper
      const helper = PLATFORM_HELPERS[platform];
      helper.validate(msg, userId);
      const requestBody = helper.buildRequestBody(userId, msg);

      const response = await messageApiClient.sendMessage(
        platform,
        senderId,
        userId,
        msg,
        requestBody
      );

      console.log(`[Unified API] ${platform} message sent:`, response.messages[0].id);
      // Only create temp message for UI feedback - server creates the real message
      const tempMessage = { ...msg, id: `temp_${Date.now()}`, status: MessageStatus.submitted };
      callback(tempMessage, null);
    } catch (error: any) {
      console.error(`[Unified API] ${platform} send failed:`, error);
      // Don't create a Firestore document here — the server already creates one
      // via MessageSenderService.processFailedStatus() with the actual API error.
      // Just update the in-memory message for UI callback.
      msg.status = MessageStatus.failed;
      msg.resultMessage = error.response?.data?.error?.message || error.message || 'Unknown error';
      msg.error = {
        errorCode: error.response?.data?.error?.code || error.response?.data?.resultCode || 'SEND_ERROR',
        errorMessage: error.response?.data?.error?.message || error.message || 'Unknown error',
      };
      callback(msg, error);
    }
  }

  /**
   * Fallback for other marketplaces (TikTok, etc.)
   */
  private static async sendMarketplaceFallback(params: SendMessageParams): Promise<void> {
    const { message: msg, companyID, room, callback, isRetry } = params;

    console.log("message createCollaborationMessage marketplace fallback", msg);
    const accountId = room?.account?.id ?? "";
    const textContent = JSON.stringify(msg.textContent).replaceAll(`"`, ``);

    try {
      await server.sendRequestMarketplaceMessage(
        msg,
        companyID,
        accountId,
        msg.collaboration.id,
        msg.room.id,
        textContent,
        (responseCode: string, responseJson: JSON) => {
          msg.status = MessageStatus.submitted;
          msg.id = `pending-${Date.now()}`;
          if (!isRetry) {
            callback(msg, null);
          }
        }
      );
    } catch (error: any) {
      msg.status = MessageStatus.failed;
      msg.resultMessage = error.message;
      msg.error = { errorCode: 'SEND_ERROR', errorMessage: error.message || 'Unknown error' };
    }
  }

  /**
   * Save failed message to Firebase
   */
  private static async saveFailedMessage(
    msg: Message,
    isRetry: boolean,
    callback: SendMessageCallback
  ): Promise<void> {
    const onMessageCreated = async (response: string | void) => {
      if (response && response !== "") {
        msg.id = response;
        callback(msg, null);
      }
    };

    if (!isRetry) {
      message.createMessage(msg).then(onMessageCreated);
    } else {
      message.retryMessage(msg).then(onMessageCreated);
    }
  }
}
